// components/source/SourceBudgetProposalCard.tsx
import { memo, useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { ListPermissionPrompt, selectListPermission } from '@/components/list/ListPermissionPrompt';
import type { SourceBudgetProposal } from '#types/types';

export const SourceBudgetProposalCard = memo(({ initial, approval = false }: { initial: SourceBudgetProposal; approval?: boolean }) => {
  const { kernel, kernelReady } = useKernel();
  const runtime = useKernelStore(state => state.sourceBudgetProposals[initial.id]);
  const activeSessionId = useKernelStore(state => state.activeChatSessionId);
  const campaignId = useKernelStore(state => state.sourceSelectedCampaignId);
  const proposal = runtime?.proposal ?? initial;
  const inScope = activeSessionId === proposal.chatSessionId && campaignId === proposal.campaignId;
  const expired = Date.parse(proposal.expiresAt) <= Date.now();
  const pending = proposal.status === 'pending';

  useEffect(() => {
    if (!kernelReady || kernel.current?.getState().sourceBudgetProposals[initial.id]?.busy) return;
    void kernel.current?.sourceBudgetProposal(initial.campaignId, {
      action: 'read', chatSessionId: initial.chatSessionId, proposalId: initial.id,
    }).catch(() => undefined);
  }, [initial.id, initial.campaignId, initial.chatSessionId, kernelReady]);

  const decide = (action: 'accept' | 'decline') => {
    const state = kernel.current?.getState();
    const current = state?.sourceBudgetProposals[initial.id];
    const currentProposal = current?.proposal;
    if (selectListPermission(state?.activeChatSessionId ?? null)(state ?? { entityKernelState: {} })) return;
    if (!currentProposal || current.busy || currentProposal.status !== 'pending'
      || Date.parse(currentProposal.expiresAt) <= Date.now()
      || state?.activeChatSessionId !== currentProposal.chatSessionId
      || state?.sourceSelectedCampaignId !== currentProposal.campaignId) return;
    void kernel.current?.sourceBudgetProposal(currentProposal.campaignId, {
      action, chatSessionId: currentProposal.chatSessionId, proposalId: currentProposal.id,
    }).catch(() => undefined);
  };

  // ONE PLAIN QUESTION (owner, 2026-10-14: "a simple natural language question, not code"): what changes in the search,
  // what it brings, and why, as a person would ask it.
  const brief = proposal.brief;
  const range = (min: number | null | undefined, max: number | null | undefined) => (min && max ? `${min} to ${max} m²` : max ? `up to ${max} m²` : `${min ?? 0} m² or more`);
  const changes = brief ? [
    brief.to.surfaceMinSqm !== undefined || brief.to.surfaceMaxSqm !== undefined
      ? `look at shops of ${range(brief.to.surfaceMinSqm ?? brief.from.surfaceMinSqm, brief.to.surfaceMaxSqm ?? brief.from.surfaceMaxSqm)} instead of ${range(brief.from.surfaceMinSqm, brief.from.surfaceMaxSqm)}` : null,
    brief.to.minimumNetworkSize !== undefined ? `accept brands with ${brief.to.minimumNetworkSize} stores or more instead of ${brief.from.minimumNetworkSize ?? 1}` : null,
  ].filter(Boolean).join(' and ') : '';
  const credits = proposal.additionalCredits > 0 ? `add ${proposal.additionalCredits} credits` : '';
  const action = [changes, credits].filter(Boolean).join(', and ');
  const question = `Shall I ${action || 'continue'}? ${brief ? `That brings ${proposal.newCandidates ?? 0} new brands to analyse` : 'That lets me analyse more brands'}, to look for one that reaches ${proposal.targetScore}.`;
  const summary = question.charAt(0).toUpperCase() + question.slice(1);
  // A free answer (owner, 2026-10-14: "we should be able to give a custom answer here"): the proposal is declined and
  // the words go to the agent as the user's message, which proposes what they asked instead.
  const replyInstead = (text: string) => {
    const state = kernel.current?.getState();
    if (!state || state.activeChatSessionId !== proposal.chatSessionId || state.sourceSelectedCampaignId !== proposal.campaignId) return;
    void kernel.current?.sourceBudgetProposal(proposal.campaignId, { action: 'decline', chatSessionId: proposal.chatSessionId, proposalId: proposal.id })
      .then(() => kernel.current?.submitChatPrompt({ promptOverride: text }))
      .catch(() => undefined);
  };
  if (approval) return <ListPermissionPrompt
    onReply={pending && inScope && !expired ? replyInstead : undefined}
    request={{ id: proposal.id, title: summary, label: 'Shortlist', items: [{ id: proposal.campaignId, name: proposal.campaignName }] }}
    disabled={!runtime?.proposal || !pending || !inScope || expired || runtime?.busy}
    error={runtime?.error || (expired ? 'Expired. Request a new credit proposal.' : undefined)}
    onAnswer={decision => decide(decision === 'once' ? 'accept' : 'decline')}
  />;

  return <div data-source-budget-proposal={proposal.id} className="text-sm text-[#B0B0B0]">
    <p>{summary}</p>
    <p role="status" className="mt-2">{pending
      ? (expired ? 'Expired. Request a new credit proposal.' : 'Awaiting your approval below.')
      : proposal.status === 'declined' ? 'Declined. No additional credits authorised.'
        : proposal.message || (proposal.status === 'accepted' ? 'Approved. The new ceiling is authorised.' : 'The continuation failed.')}</p>
  </div>;
});

SourceBudgetProposalCard.displayName = 'SourceBudgetProposalCard';
